import {
  Fastfood as FastfoodIcon,
  MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';
import { CardMedia } from '@mui/material';
import { MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import placeholderImageUrl from 'src/assets/images/placeholder.png';
import {
  CardContainer,
  CardInfoTitle,
  CardMenuIcon,
  CardText,
  EmptyProductCategory,
} from 'src/components/cardGrid';
import { DynamicTooltipChip } from 'src/components/dynamicTooltipChip/DynamicTooltipChip';
import { GenericCard } from 'src/components/genericCard';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import { useMenu } from 'src/hooks';
import { IActiveStatus } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';
import { getPreviewUrlSrc } from 'src/utils/gcf-helper';

import { ProductMenu } from '../productMenu/ProductMenu';
import { ProductCardProps } from './ProductCard.interface';
import { CardContainerBox } from './ProductCard.styled';

export const ProductCard = (props: ProductCardProps) => {
  const menu = useMenu();
  const navigate = useNavigate();
  const productCategories = props.product?.productCategories || [];
  const displayedCategory = productCategories[0];
  const remainingCategoriesCount = Math.max(0, productCategories.length - 1);
  const { isViewOnly } = useUserRole();

  const onMenuClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    menu.onOpenMenu(event.currentTarget);
  };

  const onCardClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    navigate(PATHS.PRODUCT_DETAILS(props.product.product_code));
  };

  const variations = props.product?.productVariations ?? [];
  const productVariation = variations.find(
    (variation) => variation.product_code === props.product?.product_code,
  );

  const getAssetNameWithoutExtension = (
    filename?: string,
  ): string | undefined => {
    if (!filename) return undefined;

    return filename.split('.').slice(0, -1).join('.') || filename;
  };
  const productName =
    props.product?.name_override ||
    productVariation?.name ||
    getAssetNameWithoutExtension(props.product?.asset_name?.[0]);

  return (
    <CardContainerBox role="button">
      <GenericCard
        cardMedia={
          <>
            <CardMedia
              component="img"
              height="249"
              width="249px"
              image={
                (props.product?.hqasset_name?.length ?? 0) > 0 &&
                props.product?.asset_name?.[0]
                  ? getPreviewUrlSrc(props.product.asset_name[0])
                  : placeholderImageUrl
              }
            />
            {!isViewOnly && (
              <CardMenuIcon
                $hasVisible={Boolean(menu.anchorEl)}
                onClick={onMenuClick}>
                <MoreHorizIcon />
              </CardMenuIcon>
            )}
          </>
        }
        onClick={onCardClick}
        additionalContent={
          <>
            <DynamicTooltipChip
              chipName={
                props.product.active
                  ? IActiveStatus.active
                  : IActiveStatus.inActive
              }
              color="success"
              variant="outlined"
              size="small"
            />

            <CustomTooltip
              width={500}
              placement="top"
              title={productName}
              overflowLineNumber={2}>
              <CardInfoTitle>{productName}</CardInfoTitle>
            </CustomTooltip>

            <CardContainer>
              <FastfoodIcon />{' '}
              <CardText>
                {props.product?.productVariations &&
                props.product?.productVariations?.length === 1
                  ? lang.productVariant(1)
                  : props.product?.productVariations
                  ? lang.productVariants(
                      props.product?.productVariations?.length,
                    )
                  : lang.productVariant(0)}
              </CardText>
            </CardContainer>
            <CardContainer>
              {displayedCategory && (
                <DynamicTooltipChip
                  chipName={displayedCategory.name}
                  tooltipTitle={displayedCategory.name}
                  size="small"
                  tooltipWidth={500}
                  tooltipActivationWidth={218}
                />
              )}

              {remainingCategoriesCount > 0 ? (
                <DynamicTooltipChip
                  chipName={`+${remainingCategoriesCount}`}
                  size="small"
                />
              ) : (
                <EmptyProductCategory />
              )}
            </CardContainer>
          </>
        }
        cardMenuItems={
          <>
            {!isViewOnly && (
              <ProductMenu
                anchorEl={menu.anchorEl}
                onClose={menu.onCloseMenu}
                card={props.product}
              />
            )}
          </>
        }
      />
    </CardContainerBox>
  );
};
